import React, { useState } from 'react';
import { AIOverviewData, ProcessedAIData, AIApplication } from '../types';
import { formatAITime, getAIApplicationsBySection } from '../services/aiOverviewService';
import ExportButtons from './ExportButtons';
import { exportAIOverviewToCSV, exportAIOverviewToPDF, AIOverviewExportOptions } from '../services/aiOverviewExportService';

interface AIOverviewTableProps {
  aiData: ProcessedAIData;
  exportOptions?: AIOverviewExportOptions;
}

type SortField = 'name' | 'totalTime' | 'aiTime' | 'pureAITime' | 'appCount';
type SortDirection = 'asc' | 'desc';

const AIOverviewTable: React.FC<AIOverviewTableProps> = ({ aiData, exportOptions }) => {
  const [sortField, setSortField] = useState<SortField>('totalTime');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortedUsers = [...aiData.users].sort((a, b) => {
    let aValue: number | string;
    let bValue: number | string;

    switch (sortField) {
      case 'name':
        aValue = a.teamMemberName.toLowerCase();
        bValue = b.teamMemberName.toLowerCase();
        break;
      case 'totalTime':
        aValue = a.totalCombinedAITime;
        bValue = b.totalCombinedAITime;
        break;
      case 'aiTime':
        aValue = a.totalAITime;
        bValue = b.totalAITime;
        break;
      case 'pureAITime':
        aValue = a.totalPureAITime;
        bValue = b.totalPureAITime;
        break;
      case 'appCount':
        aValue = a.aiApplications.length;
        bValue = b.aiApplications.length;
        break;
      default:
        aValue = a.totalCombinedAITime;
        bValue = b.totalCombinedAITime;
    }

    if (typeof aValue === 'string') {
      return sortDirection === 'asc' 
        ? aValue.localeCompare(bValue as string)
        : (bValue as string).localeCompare(aValue);
    } else {
      return sortDirection === 'asc' ? aValue - (bValue as number) : (bValue as number) - aValue;
    }
  });

  const toggleRowExpansion = (email: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(email)) {
      newExpanded.delete(email);
    } else {
      newExpanded.add(email);
    }
    setExpandedRows(newExpanded);
  };

  const getSortIcon = (field: SortField): string => {
    if (sortField !== field) return '↕️';
    return sortDirection === 'asc' ? '⬆️' : '⬇️';
  };

  /**
   * Handle CSV export
   */
  const handleExportCSV = () => {
    if (!exportOptions) {
      console.warn('Export options not provided for AI Overview CSV export');
      return;
    }
    exportAIOverviewToCSV(aiData, exportOptions);
  };

  /**
   * Handle PDF export
   */
  const handleExportPDF = () => {
    if (!exportOptions) {
      console.warn('Export options not provided for AI Overview PDF export');
      return;
    }
    exportAIOverviewToPDF(aiData, exportOptions);
  };

  const renderApplicationList = (applications: AIApplication[], sectionType: 'AI' | 'PURE_AI'): React.ReactNode => {
    const filteredApps = applications.filter(app => app.sectionName === sectionType);
    
    if (filteredApps.length === 0) {
      return <span className="no-apps">No {sectionType === 'PURE_AI' ? 'Pure AI' : 'AI'} apps</span>;
    }

    return (
      <div className="app-list">
        {filteredApps.map((app, index) => (
          <div key={`${app.applicationId}-${index}`} className="app-item">
            <span className="app-name">{app.applicationName}</span>
            <span className="app-time">{formatAITime(app.spentTime)}</span>
          </div>
        ))}
      </div>
    );
  };

  const renderExpandedRow = (user: AIOverviewData): React.ReactNode => {
    const { ai, pureAI } = getAIApplicationsBySection(user);
    
    return (
      <tr key={`${user.email}-expanded`} className="expanded-row">
        <td colSpan={7}>
          <div className="expanded-content">
            <div className="app-sections">
              <div className="app-section">
                <h4>🛠️ Development AI Tools</h4>
                {renderApplicationList(user.aiApplications, 'AI')}
              </div>
              <div className="app-section">
                <h4>🤖 Pure AI Tools</h4>
                {renderApplicationList(user.aiApplications, 'PURE_AI')}
              </div>
            </div>
          </div>
        </td>
      </tr>
    );
  };

  if (aiData.users.length === 0) {
    return (
      <div className="ai-overview-table-container">
        <div className="ai-overview-header">
          <h3>🤖 AI Usage Overview</h3>
          <p>No AI usage data available for the selected period.</p>
        </div>
        <div className="no-data-message">
          No team members have recorded AI tool usage in the selected time period.
        </div>
      </div>
    );
  }

  return (
    <div className="ai-overview-table-container">
      <div className="ai-overview-header">
        <div>
          <h3>🤖 AI Usage Overview</h3>
          <p>Team AI tool usage from Crossover activity tracking</p>
        </div>
        
        {exportOptions && (
          <ExportButtons
            onExportCSV={handleExportCSV}
            onExportPDF={handleExportPDF}
            disabled={aiData.users.length === 0}
            className="ai-overview-export-container"
          />
        )}
      </div>

      <div className="ai-overview-stats">
        <div className="stats-section">
          <div className="stat-item">
            <span className="stat-label">Total Users:</span>
            <span className="stat-value">{aiData.totalUsers}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Total AI Time:</span>
            <span className="stat-value">{formatAITime(aiData.overallAITime + aiData.overallPureAITime)}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Dev AI Time:</span>
            <span className="stat-value">{formatAITime(aiData.overallAITime)}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Pure AI Time:</span>
            <span className="stat-value">{formatAITime(aiData.overallPureAITime)}</span>
          </div>
        </div>

        {aiData.mostUsedAIApps.length > 0 && (
          <div className="most-used-apps-inline">
            <h4>🔥 Most Used AI Applications</h4>
            <div className="app-usage-grid-inline">
              {aiData.mostUsedAIApps.slice(0, 6).map((app, index) => (
                <div key={app.applicationName} className="app-usage-item-inline">
                  <span className="app-rank">#{index + 1}</span>
                  <span className="app-name">{app.applicationName}</span>
                  <span className="app-stats">
                    {formatAITime(app.totalTime)} • {app.userCount} users
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="table-wrapper">
        <table className="ai-overview-table">
          <thead>
            <tr>
              <th>Rank</th>
              <th onClick={() => handleSort('name')} className="sortable">
                Team Member {getSortIcon('name')}
              </th>
              <th onClick={() => handleSort('totalTime')} className="sortable">
                Total AI Time {getSortIcon('totalTime')}
              </th>
              <th onClick={() => handleSort('aiTime')} className="sortable">
                Dev AI Time {getSortIcon('aiTime')}
              </th>
              <th onClick={() => handleSort('pureAITime')} className="sortable">
                Pure AI Time {getSortIcon('pureAITime')}
              </th>
              <th onClick={() => handleSort('appCount')} className="sortable">
                Applications {getSortIcon('appCount')}
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedUsers.map((user, index) => (
              <React.Fragment key={user.email}>
                <tr className={`user-row ${index < 3 ? `rank-${index + 1}` : ''}`}>
                  <td className="rank-cell">
                    <span className="rank-number">{index + 1}</span>
                    {index === 0 && <span className="rank-badge gold">🥇</span>}
                    {index === 1 && <span className="rank-badge silver">🥈</span>}
                    {index === 2 && <span className="rank-badge bronze">🥉</span>}
                  </td>
                  <td className="team-member-cell">
                    <strong>{user.teamMemberName}</strong>
                  </td>
                  <td className="time-cell total-time">
                    <strong>{formatAITime(user.totalCombinedAITime)}</strong>
                  </td>
                  <td className="time-cell ai-time">
                    {formatAITime(user.totalAITime)}
                  </td>
                  <td className="time-cell pure-ai-time">
                    {formatAITime(user.totalPureAITime)}
                  </td>
                  <td className="app-count-cell">
                    {user.aiApplications.length} apps
                  </td>
                  <td className="actions-cell">
                    <button
                      onClick={() => toggleRowExpansion(user.email)}
                      className="expand-button"
                      title={expandedRows.has(user.email) ? 'Hide details' : 'Show details'}
                    >
                      {expandedRows.has(user.email) ? '📤' : '📥'}
                    </button>
                  </td>
                </tr>
                {expandedRows.has(user.email) && renderExpandedRow(user)}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AIOverviewTable; 